import { Dialog } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props<T extends object> {
  title: string;
  titleInProgress?: string;
  body?: React.ReactNode;
  itemsToDelete: T[];
  onDelete: (deletedItems: T[]) => Promise<any>;
  onSuccess?: (deletedItems: T[]) => void;
  onCancel?: VoidFunction;
}

export const GenericDeleteModal = <T extends object>(props: Props<T>) => {
  const { title, titleInProgress, body, itemsToDelete, onDelete, onSuccess, onCancel } = props;

  const { t } = useTranslation();
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [cancelDisabled, setCancelDisabled] = useState(false);
  const [activeTitle, setActiveTitle] = useState(title);

  let endrun = false;
  useEffect(() => {
    return () => {
      endrun = true;
    };
  }, []);

  const handleDelete = async (): Promise<any> => {
    setButtonState('pending');
    const response = onDelete(itemsToDelete); // dont await here, we want to check if its a promise or not
    if (response != null && typeof response.then === 'function') {
      // we have a promise
      setCancelDisabled(true);
      response.then((_res: any) => {
        // follow the promise here
        onSuccess?.(itemsToDelete); // FIXME if onSuccess is not define, should we reset the button state or close the modal
        //   setButtonState(undefined); // TODO verify if ok to remove in all use-cases
      });
      if (titleInProgress) {
        setTimeout(() => {
          if (endrun) {
            // ignore animation, we exited already
            return;
          }
          // switch display to progress
          if (titleInProgress) {
            setActiveTitle(titleInProgress);
          }
        }, 500); // to debounce flashing of messages before modal closes
      }
    } else {
      // no way to know when it is completed
      // setButtonState(undefined);
    }
    return false; // to keep modal open
  };

  const keyDownTargetRef = useRef<HTMLElement | null>(null);
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    // console.log('onKeyDown', e);
    if (e.key === 'Enter') {
      //   blurFocus();
      // Store the element that received the keydown event
      keyDownTargetRef.current = e.target as HTMLElement;
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    // console.log('keyup', e);
    if (e.key === 'Enter') {
      // Check if keyup target matches keydown target
      const keyUpTarget = e.target as HTMLElement;
      if (keyDownTargetRef.current !== keyUpTarget) {
        keyDownTargetRef.current = null; // Reset for next interaction
        return; // This fix when opening the modal with the keyboard on the DELETE button. The keyUp is registered on the modal's newly focussed CANCEL button
      }

      // Reset the ref after successful match
      keyDownTargetRef.current = null;

      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      if ((e.target as HTMLElement)?.id?.toString().includes('-cancel')) {
        // from cancel button, close modal
        onCancel();

        // or it could have been
        const button = document.getElementById('delete-modal-button-cancel');
        if (button) {
          e.preventDefault();
          button.click();
        }
        return;
      }
      if ((e.target as HTMLElement)?.id?.toString().includes('-submit')) {
        // from submit button, dont submit twice
        // console.log('dont submit twice');
        return;
      }
      // handleDelete(); on DELETE modals dont use Enter to submit by default (if focused on random part of the modal)
    }
  };

  /* UNUSED
  const blurFocus = (): void => {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement.blur) {
      activeElement.blur();
      activeElement.classList.remove('force-focus-visible');
    }
  };*/

  useLayoutEffect((): void => {
    const el = document.getElementById('delete-modal-button-cancel');
    if (el) {
      el.focus();
      el.classList.add('force-focus-visible'); // Add class to force focus-visible styles
    }
  }, []);

  return (
    <Dialog
      {...({ id: 'delete-modal' } as any)}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      headline={t(activeTitle, { count: itemsToDelete.length })}
      bodyText={(body ?? t('general.cannotUndo')) as unknown as string}
      onClose={onCancel}
      show={true}
      buttons={[
        { label: t('general.cancel'), disabled: cancelDisabled, id: 'delete-modal-button-cancel' },
        {
          label: t('general.deleteBtn'),
          variant: buttonState ? undefined : 'danger',
          onClick: handleDelete,
          state: buttonState,
          id: 'delete-button-submit',
        },
      ]}
    />
  );
};
